// Copyright (C) 2021-2022 Intel Corporation
// Copyright (C) 2022-2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import './styles.scss';
import React, {
    useState, useEffect, useCallback, useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Modal from 'antd/lib/modal';
import { Row, Col } from 'antd/lib/grid';
import Divider from 'antd/lib/divider';
import notification from 'antd/lib/notification';
import { QuestionCircleOutlined } from '@ant-design/icons';

import ProjectSearch from 'components/create-reference-page/project-search-field';
import CVATLoadingSpinner from 'components/common/loading-spinner';
import CVATTooltip from 'components/common/cvat-tooltip';
import { CombinedState } from 'reducers';
import { switchMoveReferenceModalVisible } from 'actions/references-actions';
import { getCore, Reference, Label } from 'cvat-core-wrapper';
import { t } from 'utils/i18n';
import LabelMapperItem, { LabelMapperItemValue } from './label-mapper-item';

const core = getCore();

function MoveReferenceModal({
    onUpdateReference,
}: {
    onUpdateReference?: (reference: Reference) => Promise<void>;
}): JSX.Element {
    const dispatch = useDispatch();
    const visible = useSelector((state: CombinedState) => state.references.moveReference.modalVisible);
    const referenceId = useSelector((state: CombinedState) => state.references.moveReference.referenceId);
    const mounted = useRef(false);

    const [referenceFetching, setReferenceFetching] = useState(false);
    const [referenceInstance, setReferenceInstance] = useState<Reference | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [projectId, setProjectId] = useState<number | null>(null);
    const [project, setProject] = useState<any>(null);
    const [labelMap, setLabelMap] = useState<{ [key: string]: LabelMapperItemValue }>({});

    const initValues = useCallback(() => {
        const labelValues: { [key: string]: LabelMapperItemValue } = {};
        if (referenceInstance) {
            referenceInstance.labels.forEach((label: Label) => {
                const labelId = label.id as number;
                labelValues[labelId] = {
                    labelId,
                    newLabelName: null,
                };
            });
        }

        setLabelMap(labelValues);
    }, [referenceInstance]);

    const onCancel = useCallback(() => {
        dispatch(switchMoveReferenceModalVisible(false));
        initValues();
        setProject(null);
        setProjectId(null);
    }, [initValues]);

    const projectsFilter = useCallback(
        (_project: { id: number }) => _project.id !== referenceInstance?.projectId,
        [referenceInstance],
    );

    const submitMove = async (): Promise<void> => {
        if (!referenceInstance) {
            throw new Error('Reference to move is not specified');
        }

        if (!projectId) {
            notification.error({ message: t('Please, select a project') });
            return;
        }

        if (Object.values(labelMap).some((map) => map.newLabelName === null)) {
            notification.error({
                message: t('Please, specify mapping for all the labels'),
            });
            return;
        }

        referenceInstance.projectId = projectId;
        referenceInstance.labels = Object.values(labelMap)
            .map((mapper) => ({
                id: mapper.labelId,
                name: mapper.newLabelName,
            }))
            .map(({ id, name }) => {
                const [label] = referenceInstance.labels.filter((_label: Label) => _label.id === id);
                (label as Label).name = name as string;
                return label;
            });

        setIsUpdating(true);
        if (onUpdateReference) {
            onUpdateReference(referenceInstance).finally(() => {
                if (mounted.current) {
                    setIsUpdating(false);
                }
            });
        } else {
            referenceInstance
                .save()
                .finally(() => {
                    if (mounted.current) {
                        setIsUpdating(false);
                    }
                })
                .catch((error: Error) => notification.error({
                    message: t('Could not update the reference'),
                    className: 'cvat-notification-notice-update-reference-failed',
                    description: error.toString(),
                }),
                );
        }

        onCancel();
    };

    useEffect(() => {
        if (visible && Number.isInteger(referenceId)) {
            setReferenceFetching(true);
            core.references
                .get({ id: referenceId })
                .then(([reference]: Reference[]) => {
                    if (mounted.current) {
                        setLabelMap({});
                        setReferenceInstance(reference);
                    }
                })
                .catch((error: Error) => notification.error({
                    message: t('Could not fetch reference from the server'),
                    description: error.toString(),
                }),
                )
                .finally(() => {
                    if (mounted.current) {
                        setReferenceFetching(false);
                    }
                });
        }
    }, [visible, referenceId]);

    useEffect(() => {
        if (projectId && referenceInstance) {
            core.projects.get({ id: projectId }).then(([_project]: any) => {
                if (_project) {
                    setProject(_project);
                    const { labels } = _project;
                    const labelValues: { [key: string]: LabelMapperItemValue } = {};
                    Object.entries(labelMap).forEach(([id, label]) => {
                        const referenceLabelName = referenceInstance.labels.filter(
                            (_label: any) => _label.id === label.labelId,
                        )[0].name;
                        const [autoNewLabel] = labels.filter((_label: any) => _label.name === referenceLabelName);
                        labelValues[id] = {
                            labelId: label.labelId,
                            newLabelName: autoNewLabel ? autoNewLabel.name : null,
                        };
                    });
                    setLabelMap(labelValues);
                }
            });
        } else {
            setProject(null);
        }
    }, [projectId, referenceInstance]);

    useEffect(() => {
        initValues();
    }, [referenceInstance]);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    return (
        <Modal
            visible={visible}
            onCancel={onCancel}
            onOk={submitMove}
            okButtonProps={{ disabled: isUpdating }}
            title={(
                <span>
                    {t('Move reference {id} to project', { id: referenceInstance?.id ?? '' })}
                    {/* TODO: replace placeholder */}
                    <CVATTooltip title={t('Some moving process description here')}>
                        <QuestionCircleOutlined className='ant-typography-secondary' />
                    </CVATTooltip>
                </span>
            )}
            className='cvat-reference-move-modal'
        >
            {referenceFetching && <CVATLoadingSpinner size='large' />}
            <Row align='middle'>
                <Col>{t('Project:')}</Col>
                <Col>
                    <ProjectSearch value={projectId} onSelect={setProjectId} filter={projectsFilter} />
                </Col>
            </Row>
            <Divider orientation='left'>{t('Label mapping')}</Divider>
            {!!Object.keys(labelMap).length &&
                !isUpdating &&
                referenceInstance?.labels.map((label: any) => (
                    <LabelMapperItem
                        label={label}
                        key={label.id}
                        projectLabels={project?.labels}
                        value={labelMap[label.id]}
                        labelMappers={Object.values(labelMap)}
                        onChange={(value) => {
                            setLabelMap({
                                ...labelMap,
                                [value.labelId]: value,
                            });
                        }}
                    />
                ))}
        </Modal>
    );
}

export default React.memo(MoveReferenceModal);
